import { useEffect, useRef } from "react";
import { basicSetup, EditorView } from "codemirror";
import { EditorState } from "@codemirror/state";
import { python } from "@codemirror/lang-python";
import { pythonEditorExtensions } from "./pythonEditorConfig";
import { readOnlyPythonEditorTheme } from "./readOnlyPythonEditorTheme";

export interface ReadOnlyPythonCodeProps {
  readonly code: string;
  readonly className?: string;
  readonly maxHeight?: string;
  readonly minHeight?: string;
  readonly ariaLabel?: string;
  /** Scroll the viewport toward the end when `code` changes (e.g. typing demos). */
  readonly scrollToEndOnChange?: boolean;
}

function emptyDoc(code: string): string {
  return code.length > 0 ? code : "# (empty)";
}

export function ReadOnlyPythonCode({
  code,
  className,
  maxHeight = "24rem",
  minHeight,
  ariaLabel = "Python source code",
  scrollToEndOnChange = false,
}: ReadOnlyPythonCodeProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<EditorView | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const view = new EditorView({
      state: EditorState.create({
        doc: emptyDoc(code),
        extensions: [
          basicSetup,
          python(),
          ...pythonEditorExtensions,
          readOnlyPythonEditorTheme,
          EditorView.editable.of(false),
          EditorState.readOnly.of(true),
          EditorView.lineWrapping,
        ],
      }),
      parent: host,
    });
    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, []);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    const next = emptyDoc(code);
    const current = view.state.doc.toString();
    if (current === next) return;

    view.dispatch({
      changes: { from: 0, to: current.length, insert: next },
      ...(scrollToEndOnChange
        ? { effects: EditorView.scrollIntoView(next.length, { y: "nearest", yMargin: 20 }) }
        : {}),
    });
  }, [code, scrollToEndOnChange]);

  return (
    <div
      ref={hostRef}
      className={className ?? "teacher-submission-code"}
      style={{ maxHeight, minHeight }}
      role="region"
      aria-label={ariaLabel}
    />
  );
}
